import { useMemo, useCallback } from 'react';
import {
  useTablePreferences,
  useUpdateTablePreferences,
} from '../../features/table-preferences/api';
import { ColumnDefinition, ColumnPreference } from '../../features/table-preferences/types';
import { arrayMove } from '@dnd-kit/sortable';

interface UseColumnPreferencesOptions<T> {
  tableId: string;
  columns: ColumnDefinition<T>[];
}

export function useColumnPreferences<T>({ tableId, columns }: UseColumnPreferencesOptions<T>) {
  const { data: preferences, isLoading } = useTablePreferences(tableId);
  const updatePreferences = useUpdateTablePreferences();

  const visibleColumns = useMemo(() => {
    if (!preferences?.preferences || preferences.preferences.length === 0) {
      return columns.filter((col) => col.visible !== false);
    }

    const prefMap = new Map<string, ColumnPreference>();
    preferences.preferences.forEach((pref) => {
      prefMap.set(pref.columnId, pref);
    });

    // Filter and sort columns based on preferences
    return columns
      .map((col) => {
        const pref = prefMap.get(col.id);
        return {
          ...col,
          _order: pref?.order ?? col.defaultOrder ?? 999,
          _visible: pref?.visible ?? col.visible ?? true,
        };
      })
      .filter((col) => col._visible)
      .sort((a, b) => a._order - b._order)
      .map(({ _order, _visible, ...col }) => col);
  }, [columns, preferences]);

  // All columns (visible and hidden) sorted by preference order.
  // Used by ColumnManager to show columns in the order they appear in the table.
  const orderedColumns = useMemo(() => {
    if (!preferences?.preferences || preferences.preferences.length === 0) {
      return columns;
    }

    const prefMap = new Map<string, ColumnPreference>();
    preferences.preferences.forEach((pref) => {
      prefMap.set(pref.columnId, pref);
    });

    return columns
      .map((col) => ({
        ...col,
        _order: prefMap.get(col.id)?.order ?? col.defaultOrder ?? 999,
      }))
      .sort((a, b) => a._order - b._order)
      .map(({ _order, ...col }) => col);
  }, [columns, preferences]);

  const columnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};

    if (!preferences?.preferences || preferences.preferences.length === 0) {
      columns.forEach((col) => {
        visibility[col.id] = col.visible !== false;
      });
    } else {
      const prefMap = new Map<string, ColumnPreference>();
      preferences.preferences.forEach((pref) => {
        prefMap.set(pref.columnId, pref);
      });

      columns.forEach((col) => {
        const pref = prefMap.get(col.id);
        visibility[col.id] = pref?.visible ?? col.visible ?? true;
      });
    }

    return visibility;
  }, [columns, preferences]);

  const toggleColumn = useCallback(
    (columnId: string) => {
      const newVisibility = !columnVisibility[columnId];

      const existingPrefs = preferences?.preferences || [];
      const prefMap = new Map(existingPrefs.map((p) => [p.columnId, p]));

      const updatedPreferences: ColumnPreference[] = columns.map((col) => {
        if (col.id === columnId) {
          return {
            columnId: col.id,
            visible: newVisibility,
            order: prefMap.get(col.id)?.order ?? col.defaultOrder ?? 999,
          };
        }

        const existing = prefMap.get(col.id);
        return {
          columnId: col.id,
          visible: existing?.visible ?? col.visible ?? true,
          order: existing?.order ?? col.defaultOrder ?? 999,
        };
      });

      updatePreferences.mutate({
        tableId,
        columns: updatedPreferences,
      });
    },
    [columnVisibility, preferences, columns, tableId, updatePreferences]
  );

  const setColumnVisibility = useCallback(
    (updates: Record<string, boolean>) => {
      const existingPrefs = preferences?.preferences || [];
      const prefMap = new Map(existingPrefs.map((p) => [p.columnId, p]));

      const updatedPreferences: ColumnPreference[] = columns.map((col) => {
        const newVisibility =
          updates[col.id] ?? prefMap.get(col.id)?.visible ?? col.visible ?? true;

        return {
          columnId: col.id,
          visible: newVisibility,
          order: prefMap.get(col.id)?.order ?? col.defaultOrder ?? 999,
        };
      });

      updatePreferences.mutate({
        tableId,
        columns: updatedPreferences,
      });
    },
    [preferences, columns, tableId, updatePreferences]
  );

  const showAllColumns = useCallback(() => {
    const allVisible: Record<string, boolean> = {};
    columns.forEach((col) => {
      allVisible[col.id] = true;
    });
    setColumnVisibility(allVisible);
  }, [columns, setColumnVisibility]);

  const hideAllColumns = useCallback(() => {
    const allHidden: Record<string, boolean> = {};
    // Keep the first column in preference order visible — can't hide everything
    const firstVisibleId = orderedColumns[0]?.id;
    columns.forEach((col) => {
      allHidden[col.id] = col.id === firstVisibleId;
    });
    setColumnVisibility(allHidden);
  }, [columns, orderedColumns, setColumnVisibility]);

  const reorderColumns = useCallback(
    (activeId: string, overId: string) => {
      if (activeId === overId) return;

      const oldIndex = orderedColumns.findIndex((c) => c.id === activeId);
      const newIndex = orderedColumns.findIndex((c) => c.id === overId);

      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(orderedColumns, oldIndex, newIndex);

      const existingPrefs = preferences?.preferences || [];
      const prefMap = new Map(existingPrefs.map((p) => [p.columnId, p]));

      const updatedPreferences: ColumnPreference[] = reordered.map((col, index) => ({
        columnId: col.id,
        visible: prefMap.get(col.id)?.visible ?? col.visible ?? true,
        order: index,
      }));

      updatePreferences.mutate({
        tableId,
        columns: updatedPreferences,
      });
    },
    [orderedColumns, preferences, tableId, updatePreferences]
  );

  return {
    visibleColumns,
    orderedColumns,
    columnVisibility,
    isLoading,
    toggleColumn,
    setColumnVisibility,
    showAllColumns,
    hideAllColumns,
    isUpdating: updatePreferences.isPending,
    reorderColumns,
  };
}
